import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { useCourse } from "../context/CourseContext";
import { Input } from "./ui/input";

export default function SearchOverlay({ open, onClose }) {
  const { course } = useCourse();
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const results = useMemo(() => {
    if (!course || !q.trim()) return [];
    const needle = q.trim().toLowerCase();
    const out = [];
    for (const m of course.modules) {
      if (m.title.toLowerCase().includes(needle) || m.subtitle.toLowerCase().includes(needle)) {
        out.push({ type: "module", moduleId: m.id, title: m.title, subtitle: m.subtitle });
      }
      for (const t of m.topics) {
        if (
          t.title.toLowerCase().includes(needle) ||
          (t.blocks || []).some((b) => (b.text || "").toLowerCase().includes(needle))
        ) {
          out.push({
            type: "topic",
            moduleId: m.id,
            topicId: t.id,
            title: t.title,
            subtitle: m.title,
          });
        }
      }
      if (out.length > 40) break;
    }
    return out.slice(0, 40);
  }, [course, q]);

  if (!open) return null;

  const goTo = (r) => {
    onClose?.();
    if (r.type === "module") navigate(`/module/${r.moduleId}`);
    else navigate(`/module/${r.moduleId}#${r.topicId}`);
  };

  return (
    <div
      data-testid="search-overlay"
      className="fixed inset-0 z-[100] bg-background/70 backdrop-blur-md flex items-start justify-center pt-24 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 border-b border-border">
          <SearchIcon className="h-5 w-5 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search modules and topics…"
            data-testid="search-input"
            className="border-0 shadow-none focus-visible:ring-0 text-base h-14"
          />
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted"
            data-testid="close-search-btn"
            aria-label="Close search"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {q && results.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">No matches for "{q}"</div>
          )}
          {!q && (
            <div className="p-6 text-sm text-muted-foreground">
              Try searching for "correlation", "SQL", "A/B testing"…
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.moduleId}-${r.topicId || i}`}
              onClick={() => goTo(r)}
              data-testid={`search-result-${i}`}
              className="w-full text-left px-5 py-3 border-b border-border last:border-0 hover:bg-muted/60 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                    r.type === "module"
                      ? "bg-brand-amber/15 text-brand-amber"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {r.type}
                </span>
                <span className="text-sm font-medium">{r.title}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 ml-[62px]">{r.subtitle}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
